/**
 * One figure as a card that links to its page: an icon and label over the
 * value and its unit. Spec beside it in stat-card.md.
 *
 * @module components/ui/stat-card
 */
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type { ReactNode } from "react";

import { cn } from "~/lib/utils";
import { Tip } from "./tip";

/**
 * The surface stat cards and suggestion chips share: a 16px radius and a 1px hairline on the card fill, with no
 * shadow, so the cards recede behind the list that holds the work.
 */
export const CARD_SURFACE_CLASS =
  "rounded-2xl border border-border/70 bg-card outline-none transition-[background-color,border-color] duration-120 ease-[ease-out] hover:bg-accent focus-visible:shadow-[0_0_0_2px_var(--ring)]";

export function StatCard({
  icon,
  label,
  detail,
  value,
  unit,
  className,
  render,
  ...props
}: useRender.ComponentProps<"a"> & {
  /** 14px; an animated icon plays while the card is hovered (it is the nearest `group`). */
  readonly icon?: ReactNode;
  readonly label: string;
  /** The label's tooltip: the figure as a sentence. */
  readonly detail?: string;
  readonly value: string;
  readonly unit?: string;
}) {
  const defaultProps = {
    className: cn(CARD_SURFACE_CLASS, "group group/stat-card flex min-w-0 flex-col gap-1 px-3.5 py-2.5", className),
    children: (
      <>
        <Tip label={detail}>
          <span className="flex min-w-0 items-center gap-2 text-xs text-muted-foreground group-hover/stat-card:text-foreground">
            {icon ? (
              <span className="flex size-3.5 shrink-0 items-center justify-center [&>svg]:size-3.5 [&>svg]:stroke-[1.75]">{icon}</span>
            ) : null}
            <span className="truncate">{label}</span>
          </span>
        </Tip>
        <span className="flex min-w-0 items-baseline gap-1.5">
          <span className="shrink-0 text-[17px] font-semibold leading-6 tracking-tight tabular-nums text-foreground">{value}</span>
          {unit ? <span className="min-w-0 truncate text-xs text-muted-foreground">{unit}</span> : null}
        </span>
      </>
    ),
  };
  return useRender({ defaultTagName: "a", props: { ...mergeProps<"a">(defaultProps, props), "data-slot": "stat-card" }, render });
}
